import { ListBulletIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import type { FilterOption } from '@/components/filters/types';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { NoData } from '@/components/legacy-ui/NoData';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ViewToggle, type ViewToggleOption } from '@/components/ViewToggle';
import { useAutoFillNextPage } from '@/hooks';
import { useScroll } from '@/hooks/useScroll';
import { Webpage } from '@/types';

import { FocusReview } from './components/FocusReview';
import { WebpageGallery } from './components/WebpageGallery';
import { WebpageTable } from './components/WebpageTable';
import { useWebpageFilters } from './hooks/useWebpageFilters';
import { useWebpages } from './hooks/useWebpages';

const VIEWED_FILTER_OPTIONS: FilterOption[] = [
  { id: 'viewed', label: 'Viewed', value: 'viewed' },
  { id: 'not-viewed', label: 'Not Viewed', value: 'not-viewed' },
];

/** The two Screenshots views: Contact Sheet (grid) ↔ Triage Inbox (table). */
type WebpageView = 'grid' | 'table';

const VIEW_OPTIONS: ViewToggleOption<WebpageView>[] = [
  { value: 'grid', label: 'Contact Sheet', icon: Squares2X2Icon },
  { value: 'table', label: 'Triage Inbox', icon: ListBulletIcon },
];

interface WebpagesContentProps {
  viewMode: string;
  setViewMode: (mode: string) => void;
}

export function WebpagesContent({
  viewMode,
  setViewMode,
}: WebpagesContentProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);
  const gridScrollRef = useRef<HTMLDivElement>(null);

  // The Triage Inbox (table) is the currently-active view. Drives `inboxVisible`
  // (gates the inbox J/K/V/O global hotkeys so they are inert under the grid).
  const inboxVisible = viewMode === 'table';

  // Shared review state, lifted into the host so the grid, the inbox, and the
  // Focus Review lightbox all read/write ONE active index (FIX #2).
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusOpen, setFocusOpen] = useState(false);
  // Snapshot of the review list captured at the moment Focus Review opens. The
  // lightbox indexes THIS frozen array instead of the live, filter-shrinking
  // `webpages`, so activating a capture under the "Not Viewed" filter — which
  // marks it viewed and drops it from `webpages` on the next render — cannot
  // shift the lightbox onto the wrong capture or cascade-mark the rest viewed
  // (ENG-4341 R2A). `openFocus` (defined below) fills this at open time.
  const [reviewList, setReviewList] = useState<Webpage[]>([]);

  const {
    sort,
    setSort,
    filters,
    setFilters,
    filterByViewed,
    hasActiveFilters,
    clearFilters,
    markViewed,
    isViewed,
  } = useWebpageFilters();

  // Every sort key is a real `keyof Webpage` ordered by the server query —
  // `'riskScore'` included (mapped to the backend `risk_score` inside
  // useWebpages, ENG-4395). No client-side re-sort remains.
  const {
    data: loadedWebpages,
    status,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWebpages({
    search: debouncedSearch,
    orderBy: sort.orderBy,
    descending: sort.descending,
    typeFilter: filters.type,
    serviceFilter: filters.service,
  });

  const webpages = useMemo(
    () => filterByViewed(loadedWebpages),
    [loadedWebpages, filterByViewed]
  );

  // Activation batches `markViewed` THEN `onOpenFocus` inside one React event
  // handler, so when `openFocus` runs `webpages` still contains the clicked item
  // at `index` (the markViewed state update has not re-rendered yet).
  // Snapshotting `webpages` here therefore freezes the CORRECT list+index into
  // `reviewList`, immune to the viewed-filter shrink that lands on the next
  // render (ENG-4341 R2A).
  const openFocus = useCallback(
    (index: number) => {
      setReviewList(webpages);
      setActiveIndex(index);
      setFocusOpen(true);
    },
    [webpages]
  );

  // While the lightbox is open it reads the frozen snapshot; otherwise every
  // view reads the live, filter-shrinking `webpages`. Render-time state
  // adjustment (React-sanctioned "adjust state when derived data changes" — no
  // effect, converges next render), NOT a useEffect.
  const focusList = focusOpen ? reviewList : webpages;
  if (focusOpen && loadedWebpages.length === 0) {
    // Close the open lightbox ONLY when the underlying LOADED data is genuinely
    // empty — captures deleted / searched away, the real "vanished list" case
    // (FIX-E). Keyed on `loadedWebpages` (pre-viewed-filter), NOT the filtered
    // `webpages`: a viewed-filter shrink that empties `webpages` must NOT close
    // the lightbox, because the clicked capture is still in `loadedWebpages` and the
    // lightbox reads its frozen `reviewList` snapshot. That covers opening the
    // LAST/only unviewed capture under the "Not Viewed" filter (codex 3567488579,
    // generalizing R2A to the single-item case) and an external non-empty live
    // shrink (F1) — both keep the lightbox open on the snapshot.
    setFocusOpen(false);
  } else if (focusList.length > 0 && activeIndex > focusList.length - 1) {
    // Keep the shared active index in range of the ACTIVE list: the frozen
    // snapshot while open (so the Not-Viewed shrink can't move it), the live list
    // when closed (so the table J/K cursor clamps after a filter shrinks it).
    // Guarded on a non-empty list so a closed, emptied `webpages` never drives
    // the index negative.
    setActiveIndex(focusList.length - 1);
  }

  // Single validated live-site open (security R1/R2/R3). This is the ONLY
  // `window.open` in the entire `sections/webpages/**` tree — every view routes
  // its live-site affordance through here via `onOpenLiveSite(webpage)`.
  //   • reject unparseable URLs (untrusted `webpage.url`, R2)
  //   • http/https allowlist ONLY — blocks `javascript:`/`data:` DOM-XSS (R2)
  //   • `noopener,noreferrer` — blocks reverse-tabnabbing + Referer leak (R1)
  //   • mark viewed ONLY AFTER the URL validates (R3) — a rejected URL is never
  //     recorded as viewed. Keyed by `webpage.key` (the viewed-tracking key),
  //     which is why the helper takes the whole webpage, not a bare url string.
  const openLiveSite = useCallback(
    (webpage: Webpage) => {
      let parsed: URL;
      try {
        parsed = new URL(webpage.url);
      } catch {
        return;
      }
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
      markViewed(webpage.key);
      window.open(parsed.href, '_blank', 'noopener,noreferrer');
    },
    [markViewed]
  );

  const typeOptions: FilterOption[] = useMemo(() => {
    const types = new Set(loadedWebpages.map(wp => wp.type).filter(Boolean));
    return Array.from(types)
      .sort()
      .map(type => ({
        id: type,
        label:
          type === 'authentication'
            ? 'Login'
            : type === 'llm'
              ? 'LLM'
              : type.charAt(0).toUpperCase() + type.slice(1),
        value: type,
      }));
  }, [loadedWebpages]);

  const serviceOptions: FilterOption[] = useMemo(() => {
    const services = new Set(
      loadedWebpages
        .map(wp => wp.service)
        .filter((s): s is string => Boolean(s))
    );
    return Array.from(services)
      .sort()
      .map(service => ({
        id: service,
        label: service.charAt(0).toUpperCase() + service.slice(1),
        value: service,
      }));
  }, [loadedWebpages]);

  useScroll(gridScrollRef, fetchNextPage, hasNextPage);

  // Re-arm auto-fill when the user changes a client-side filter/sort (NOT on
  // page arrival). Computed once and shared by BOTH the grid hook (`resetKey`)
  // and `<WebpageTable autoFillResetKey>` so the two views re-arm identically.
  const autoFillResetKey = `${filters.viewed.join(',')}|${filters.type.join(',')}|${filters.service.join(',')}|${sort.orderBy}|${String(sort.descending)}`;

  // FIX B (ENG-4416): when a client-side filter (e.g. "Not Viewed") shrinks the
  // rendered list below the grid container height, the passive `useScroll`
  // listener never fires (no overflow ⇒ no scroll event). This hook pages until
  // the container overflows or pages run out — capped at AUTO_FILL_MAX_ROUNDS,
  // after which the "Load more" button below is the human brake.
  const { capReached: gridCapReached, loadMore: gridLoadMore } =
    useAutoFillNextPage(gridScrollRef, {
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      enabled: viewMode === 'grid' && status === 'success',
      contentKey: webpages.length,
      resetKey: autoFillResetKey,
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <TableControlsContainer
        filterLabel="Filters:"
        filters={
          <>
            <FilterDropdown
              id="viewed"
              label="Viewed"
              options={VIEWED_FILTER_OPTIONS}
              selectedValues={filters.viewed}
              onApply={selected => {
                setFilters(prev => ({
                  ...prev,
                  viewed: selected.map(o => o.value as string),
                }));
              }}
              onClear={() => {
                setFilters(prev => ({ ...prev, viewed: [] }));
              }}
              showSelectAll={false}
              maxSelections={1}
              width={200}
            />
            <FilterDropdown
              id="type"
              label="Type"
              options={typeOptions}
              selectedValues={filters.type}
              onApply={selected => {
                setFilters(prev => ({
                  ...prev,
                  type: selected.map(o => o.value as string),
                }));
              }}
              onClear={() => {
                setFilters(prev => ({ ...prev, type: [] }));
              }}
              showSelectAll={false}
              maxSelections={1}
              width={200}
            />
            <FilterDropdown
              id="service"
              label="Service"
              options={serviceOptions}
              selectedValues={filters.service}
              onApply={selected => {
                setFilters(prev => ({
                  ...prev,
                  service: selected.map(o => o.value as string),
                }));
              }}
              onClear={() => {
                setFilters(prev => ({ ...prev, service: [] }));
              }}
              showSelectAll={false}
              maxSelections={1}
              width={250}
            />
            {hasActiveFilters && (
              <ClearFiltersButton
                onClick={() => {
                  clearFilters();
                }}
              />
            )}
          </>
        }
        filtersRight={
          <div className="flex items-center gap-2">
            <SearchBar
              size="sm"
              className="h-fit w-64"
              value={search}
              onChange={(value: string) => {
                setSearch(value);
              }}
              placeholder="Search by URL"
              disabled={status === 'pending'}
            />
            {/* Shared segmented control — preserves the `?type=` deep-link
                because `setViewMode` is the parent's `setTypeParam` (writes the
                `type` URL param) exactly as the old inline buttons did. */}
            <ViewToggle<WebpageView>
              value={viewMode === 'table' ? 'table' : 'grid'}
              onChange={setViewMode}
              options={VIEW_OPTIONS}
              aria-label="Screenshot view"
            />
          </div>
        }
      />

      <div className="min-h-0 flex-1">
        {viewMode === 'grid' ? (
          <div ref={gridScrollRef} className="h-full overflow-auto">
            {status === 'pending' ? (
              // Skeleton cells mirroring the real gallery grid + cell footprint
              // (design-spec §5.1) — NOT a centered spinner. `motion-safe:` keeps
              // reduced-motion users pulse-free.
              <div
                aria-hidden
                className="grid grid-cols-1 gap-4 overflow-x-hidden p-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-chariot-background-secondary h-40 rounded-md motion-safe:animate-pulse"
                  />
                ))}
              </div>
            ) : status === 'error' ? (
              // Inline actionable card (design-spec §5.1): error headline + body +
              // a Retry that re-runs the query.
              <div className="flex h-full items-center justify-center p-4">
                <div className="border-chariot-stroke bg-chariot-background-primary flex max-w-sm flex-col items-center gap-2 rounded-md border p-6 text-center">
                  <p className="text-error text-sm font-bold">
                    Failed to load screenshots
                  </p>
                  <p className="text-chariot-text-secondary text-sm">
                    Something went wrong while fetching screenshots. Check your
                    connection and try again.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="border-chariot-stroke text-chariot-text-primary hover:bg-chariot-background-secondary focus-visible:ring-chariot-brand-primary mt-2 flex min-h-11 items-center rounded-md border px-3 py-1.5 text-sm font-medium focus:outline-hidden focus-visible:ring-2"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : webpages.length === 0 ? (
              <NoData
                title="No screenshots captured yet"
                description="Screenshots appear here as your web attack surface is scanned."
              />
            ) : (
              <>
                <WebpageGallery
                  webpages={webpages}
                  markViewed={markViewed}
                  isViewed={isViewed}
                  onOpenFocus={openFocus}
                  onOpenLiveSite={openLiveSite}
                />
                {isFetchingNextPage && (
                  <div className="flex justify-center py-4">
                    <Spinner />
                  </div>
                )}
                {hasNextPage === true &&
                  gridCapReached &&
                  !isFetchingNextPage && (
                    <div className="flex justify-center py-4">
                      <Button variant="secondary" onClick={gridLoadMore}>
                        Load more
                      </Button>
                    </div>
                  )}
              </>
            )}
          </div>
        ) : (
          <WebpageTable
            webpages={webpages}
            status={status}
            error={error}
            sort={sort}
            onSort={setSort}
            markViewed={markViewed}
            isViewed={isViewed}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onOpenFocus={openFocus}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            onOpenLiveSite={openLiveSite}
            inboxVisible={inboxVisible}
            autoFillResetKey={autoFillResetKey}
          />
        )}
      </div>

      <FocusReview
        webpages={focusList}
        index={activeIndex}
        open={focusOpen}
        onClose={() => setFocusOpen(false)}
        onIndexChange={setActiveIndex}
        onOpenLiveSite={openLiveSite}
        markViewed={markViewed}
        isViewed={isViewed}
      />
    </div>
  );
}
